/**
 * useSafeAction Hook
 * 
 * Production-safe action guard to prevent:
 * - Double execution
 * - Race conditions
 * - Rapid repeat clicks
 * - React StrictMode double-invoke
 * 
 * Usage:
 *   const { execute, isExecuting, reset } = useSafeAction();
 *   
 *   const handleClick = async () => {
 *     await execute('add-item', async () => {
 *       await addItemToList(...);
 *     });
 *   };
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { FEATURE_FLAGS } from '@/config/featureFlags';

/**
 * Generate a unique session-scoped client ID for idempotency
 * This ID is deterministic per action within the same session
 */
const generateClientId = (actionKey, payload = '') => {
  const timestamp = Date.now();
  const payloadHash = typeof payload === 'string' 
    ? payload.slice(0, 50) 
    : JSON.stringify(payload).slice(0, 50);
  return `${actionKey}_${payloadHash}_${timestamp}`;
};

/**
 * Session-scoped deduplication cache
 * Tracks recently executed actions to prevent duplicates
 */
const recentActionsCache = new Map();
const DEDUPE_WINDOW_MS = 2000; // 2 second window for deduplication

/**
 * Clear stale entries from the cache
 */
const cleanupCache = () => {
  const now = Date.now();
  for (const [key, timestamp] of recentActionsCache.entries()) {
    if (now - timestamp > DEDUPE_WINDOW_MS) {
      recentActionsCache.delete(key);
    }
  }
};

/**
 * Check if an action was recently executed (within dedupe window)
 */
const wasRecentlyExecuted = (dedupeKey) => {
  cleanupCache();
  const lastExecuted = recentActionsCache.get(dedupeKey);
  if (!lastExecuted) return false;
  return Date.now() - lastExecuted < DEDUPE_WINDOW_MS;
};

/**
 * Mark an action as executed
 */
const markAsExecuted = (dedupeKey) => {
  recentActionsCache.set(dedupeKey, Date.now());
};

/**
 * Safe Action Hook
 * @param {Object} options - Configuration options
 * @param {number} options.debounceMs - Minimum time between executions (default: 300ms)
 * @param {boolean} options.enableDeduplication - Enable session deduplication (default: true)
 * @returns {Object} Hook state and methods
 */
export function useSafeAction(options = {}) {
  const {
    debounceMs = 300,
    enableDeduplication = true,
  } = options;

  const [isExecuting, setIsExecuting] = useState(false);
  const [lastError, setLastError] = useState(null);
  const lastExecutionRef = useRef(0);
  const executionIdRef = useRef(0);
  const mountedRef = useRef(true);

  // Track mounted state to prevent updates after unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * Execute an action with guards
   * @param {string} actionKey - Unique identifier for this action type
   * @param {Function} action - Async action to execute
   * @param {Object} options - Execution options
   * @param {string} options.dedupeKey - Custom deduplication key (optional)
   * @returns {Promise<{success: boolean, result?: any, error?: Error, skipped?: boolean}>}
   */
  const execute = useCallback(async (actionKey, action, execOptions = {}) => {
    // Guard: Feature flag check
    if (!FEATURE_FLAGS.ENABLE_IDEMPOTENT_WRITES) {
      // Feature disabled - execute without guards (fallback)
      try {
        const result = await action();
        return { success: true, result };
      } catch (error) {
        return { success: false, error };
      }
    }

    const { dedupeKey } = execOptions;
    const now = Date.now();
    const currentExecutionId = ++executionIdRef.current;

    // Guard 1: Already executing
    if (isExecuting) {
      if (import.meta.env.DEV) {
        console.log(`[SafeAction] Blocked (already executing): ${actionKey}`);
      }
      return { success: false, skipped: true, reason: 'already_executing' };
    }

    // Guard 2: Debounce check
    if (now - lastExecutionRef.current < debounceMs) {
      if (import.meta.env.DEV) {
        console.log(`[SafeAction] Blocked (debounce): ${actionKey}`);
      }
      return { success: false, skipped: true, reason: 'debounced' };
    }

    // Guard 3: Deduplication check
    const effectiveDedupeKey = dedupeKey || actionKey;
    if (enableDeduplication && wasRecentlyExecuted(effectiveDedupeKey)) {
      if (import.meta.env.DEV) {
        console.log(`[SafeAction] Blocked (duplicate): ${actionKey}`);
      }
      return { success: false, skipped: true, reason: 'duplicate' };
    }

    // Execute the action
    try {
      setIsExecuting(true);
      lastExecutionRef.current = now;
      
      if (enableDeduplication) {
        markAsExecuted(effectiveDedupeKey);
      }

      const result = await action();

      // Only update state if this is still the current execution and component is mounted
      if (currentExecutionId === executionIdRef.current && mountedRef.current) {
        setLastError(null);
      }

      return { success: true, result };
    } catch (error) {
      if (currentExecutionId === executionIdRef.current && mountedRef.current) {
        setLastError(error);
      }
      
      // Log non-PII error info for diagnostics
      if (import.meta.env.DEV) {
        console.error(`[SafeAction] Error in ${actionKey}:`, error.message);
      }

      return { success: false, error };
    } finally {
      if (currentExecutionId === executionIdRef.current && mountedRef.current) {
        setIsExecuting(false);
      }
    }
  }, [isExecuting, debounceMs, enableDeduplication]);

  /**
   * Reset the hook state
   */
  const reset = useCallback(() => {
    setIsExecuting(false);
    setLastError(null);
    lastExecutionRef.current = 0;
  }, []);

  return {
    execute,
    isExecuting,
    lastError,
    reset,
    // Expose for direct use
    generateClientId,
  };
}

/**
 * Create a deduplication key for item-based actions
 * @param {string} actionType - Type of action (e.g., 'add-item')
 * @param {string} itemName - Item name
 * @param {string} listId - List ID
 * @returns {string} Deduplication key
 */
export const createItemDedupeKey = (actionType, itemName, listId) => {
  const normalizedName = (itemName || '').toLowerCase().trim();
  return `${actionType}_${listId}_${normalizedName}`;
};

/**
 * Create a deduplication key for auth actions
 * @param {string} authType - Type of auth (e.g., 'google-signin')
 * @param {string} source - Source of auth trigger (e.g., 'cta', 'login', 'signup')
 * @returns {string} Deduplication key
 */
export const createAuthDedupeKey = (authType, source) => {
  return `${authType}_${source}_${Date.now()}`;
};

export default useSafeAction;
